import { ICustomersFiltersDTO } from 'src/sagas/customersSaga';
import { UserRole } from '../../reducers/userReducer';
import { IPosition } from '../positions/position';
import { TransactionStatus } from '../transactions/types';
import { IWallet, WalletPlatform } from '../wallets/types';
import { SalesAgentRoles } from './AssignableCustomers';
import { IGroup, ISwap, ITradeCommission } from '../groups/types';

export enum TradingType {
  Netting = 'NETTING',
  Hedging = 'HEDGING',
}

export enum WalletType {
  Trading = 'TRADING',
  PropChallenge = 'PROP_CHALLENGE',
  PropFunded = 'PROP_FUNDED',
  Affiliate = 'AFFILIATE',
}

export enum CreateWalletType {
  Trading = 'TRADING',
  PropTrading = 'PROPTRADING',
  MT5 = 'MT5',
}

export enum SecurityLevel {
  Regular = 1,
  Sensitive = 2,
}

export interface ICreateWallet {
  customerId: string;
  groupId?: number;
  /** MT5 only: the group name as reported by the MT5 server. MT5 groups have no local id. */
  mt5Group?: string;
  leverage?: number;
  isDemo?: boolean;
  tradingType?: TradingType;
  funnelId?: string;
  platform?: WalletPlatform;
}

export interface ICustomerModalsShow {
  affiliatesModalShow: boolean;
  salesPersonModalShow: boolean;
  changeCommissionSchemeShow: boolean;
  updatePasswordShow: boolean;
  confirmEmailShow: boolean;
  confirmBarShow: boolean;
  callCustomerModalShow: boolean;
  emailCustomerModalShow: boolean;
  smsCustomerModalShow: boolean;
  whatsAppCustomerModalShow: boolean;
  mt5PushCustomerModalShow: boolean;
  assignSalesStatusShow: boolean;
  removeRiskStatusShow: boolean;
  messageModalShow: boolean;
  changeSecurityLevelShow: boolean;
  depositLinkModalShow: boolean;
  toggleTestFlagShow: boolean;
}

export const initialModalsState: ICustomerModalsShow = {
  affiliatesModalShow: false,
  salesPersonModalShow: false,
  changeCommissionSchemeShow: false,
  updatePasswordShow: false,
  confirmEmailShow: false,
  confirmBarShow: false,
  callCustomerModalShow: false,
  emailCustomerModalShow: false,
  smsCustomerModalShow: false,
  whatsAppCustomerModalShow: false,
  mt5PushCustomerModalShow: false,
  assignSalesStatusShow: false,
  removeRiskStatusShow: false,
  messageModalShow: false,
  changeSecurityLevelShow: false,
  depositLinkModalShow: false,
  toggleTestFlagShow: false,
};

export interface IMarkup {
  groupId: string;
  instrumentSymbol: string;
  bid: number;
  ask: number;
}

export interface ICommission {
  groupId: string;
  symbol: string;
  open: string;
  close: string;
}

export interface IGroupWithExtraData extends IGroup {
  markups: Array<IMarkup>;
  commissions: Array<ITradeCommission>;
  swaps: Array<ISwap>;
  stopOutLevel: number;
  marginCallLevel: number;
}

export interface IKYCDocument {
  url: string;
  token: string;
}

export interface ITransaction {
  id: string;
  type: string;
  amount: number;
  currency: string;
  method: string;
  status: TransactionStatus;
  createdAt: Date;
  creditedAt: Date;
  netAmountInUSD: number;
  wallet: IWallet;
}

export interface ICustomerTransaction extends ITransaction {
  walletId: number | null;
  propFunnelId: string | null;
  provider: string;
  providerRef: string | null;
}

export interface ICustomerTransactionsQuery {
  transactions: Array<ITransaction>;
  count: number;
}

export enum MessageType {
  SMS = 'SMS',
  Push = 'PUSH',
}

interface ICommentingCustomer {
  id: string;
  name: string;
  avatarUrl: string;
}

export interface IComment {
  comment: string;
  createdAt: string;
  id: number;
  commentingCustomer: ICommentingCustomer;
}

export interface ICommissionScheme {
  name: string;
}

interface ISalesDesk {
  id: string | null;
  name: string | null;
}

interface ISalesPerson {
  id: string | null;
  name: string | null;
}

export interface ICustomer {
  affiliateId: string | null;
  availableContactMethods: Array<string>;
  communicationLanguage: string;
  referrer: string;
  confirmedAt: string | null;
  country: string;
  countryName: string;
  createdAt: string | null;
  depositTotal: number;
  documentCount: number;
  email: string;
  firstname: string;
  id: string;
  numericId: number;
  isBookmarked: boolean;
  kycReviewedById: string | null;
  kycReviewedByName: string;
  kycStatus: string | null;
  lastTradingActivityDate: string | null;
  lastname: string | null;
  level: number;
  mobilenumber: string;
  name: string;
  notes: string;
  roles: Array<UserRole>;
  status: string;
  totalLogins: null | number;
  totalReferrals: number;
  appInstalledAt: Date | string | null;
  lastLoginDate: Date | string | null;
  commissionScheme: ICommissionScheme | null;
  totalCoinsEarned: number;
  totalVolumeTraded: number;
  closedPnL?: number;
  percent: number | undefined;
  tradeVolumeOpen: number;
  tradeVolumeLastWeek: number;
  conversionAgent: ISalesPerson | null;
  retentionAgent: ISalesPerson | null;
  salesDesk: ISalesDesk | null;
  salesStatusName: string | null;
  hasFTD: boolean;
  lastActive: string | null;
  riskStatus: 'NONE' | 'REVIEW';
  withdrawalTotal: number;
  netDeposit?: number;
  source: string | null;
  commissionsGranted?: number;
  commissionsEarned?: number;
  terminationRequestedAt: string | null;
  groups: Array<number>;
  securityLevel: SecurityLevel;
  isTest: boolean;
}

export interface ICustomersQuery {
  customers: [];
  pages: number;
}

export interface IAffiliateQueryParams {
  search: string | null;
  country: string | null;
  selectedStatuses: Array<string> | null;
  limit: number;
  offset: number;
  sortBy: string | null;
}

export const customerStatuses = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'downloaded', label: 'Downloaded' },
  { value: 'push', label: 'Push' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'DISABLED', label: 'Disabled' },
];

export interface ISalesPersonCustomer {
  id: string;
  level: string;
  name: string;
  totalDeposit: string;
  totalWithdrawal?: string;
  netDeposit?: string;
  tradingVolume: string;
}

export interface ISalesPersonDetailsQuery {
  customer: {
    id: string;
    name: string;
  };
  customers: Array<ISalesPersonCustomer>;
  pages: number;
}

export interface ISalesPeopleRow {
  id: string;
  name: string;
  totalDeposit: string | number;
  totalWithdrawal?: string | number;
  netDeposit?: string | number;
  /** Sum of balances of customers under this sales person. */
  balance?: number;
  totalTradingVolume: number;
  usersUnder: string | number;
}

export interface ISalesPeopleQuery {
  pages: number;
  salesPeople: Array<ISalesPeopleRow>;
}

export interface ICallDetail {
  duration: number;
  answered: boolean;
  recordingFileLink: string;
}

export interface IMessageDetail {
  readAt: Date;
  viaPush: boolean;
  subject: string;
  text: string;
}

export interface IEmailDetail {
  subject: string;
  body: string;
}

export interface ICommentDetail {
  comment: string;
}

export interface ISmsWhatsAppDetail {
  body: string;
  direction: 'INBOUND' | 'OUTBOUND';
  channel: 'SMS' | 'WHATSAPP';
  deliveryStatus?: string | null;
  linkClickedAt?: string | null;
}

export interface IMt5PushDetail {
  text: string;
}

export interface ICommunication {
  date: string;
  detail: IEmailDetail | IMessageDetail | ICallDetail | ICommentDetail | ISmsWhatsAppDetail | IMt5PushDetail;
  from: string;
  fromName: string;
  toName: string;
  fromCustomerId: string;
  toCustomerId: string;
  id: string;
  type: 'CALL' | 'MESSAGE' | 'EMAIL' | 'COMMENT' | 'SMS' | 'WHATSAPP' | 'MT5_PUSH';
  userType: 'customer' | 'lead';
}

export interface ITableField {
  key: string;
  label: string;
  sorter?: boolean;
}

export interface ICallServiceAvailable {
  available: boolean;
  provider: string;
}

export interface ISalesPersonCommunicationQuery {
  communications: Array<ICommunication>;
  count: number;
}

export interface IUpdateCustomerParams {
  fullname?: string;
  email?: string;
  mobilenumber?: string;
  country?: string;
  roles?: Array<string>;
  primaryWalletId?: number | null;
  newPassword?: string | number;
  repeatPassword?: string | number;
  commissionSchemeId?: number;
  salesStatusId?: number;
  salesDeskId?: string;
  rejectTerminationRequest?: boolean;
  salesPersonId?: string | null;
  salesAgentRole?: SalesAgentRoles;
  removeRiskStatus?: boolean;
  newReferrerId?: string;
  customerStatus?: CustomerStatus;
  confirmEmail?: boolean;
  groups?: Array<number>;
  securityLevel?: number;
  isTest?: boolean;
}

export interface ILoyaltyLevelDetailsQuery {
  awardedAt: string;
  coinsRequiredToRetain: number;
  /** coins required to reach the next level. this will be null if the user is on the topmost level. */
  coinsUntilNextLevel: number | null;
  currentLevel: number;
  currentLevelName: string;
  totalCoinsLifeTime: number;
  totalCoinsMonth: number;
  validUntil: string;
}

export interface ICustomerPositionsQuery {
  positions: Array<IPosition>;
  count: number;
}

export interface IReferralNode {
  id: string;
  name: string;
  children: Array<IReferralNode>;
  nodeProps: {
    name: string;
  };
  textProps: {
    name: string;
    x: number;
    y: number;
  };
}

export interface IRiskEvent {
  createdAt: string;
  eventType: string;
  message: string;
}

export interface ICustomerOnboardingStep {
  completionDate: string | null;
  stage: string;
  step: string;
}
export interface IAffiliateInfo {
  name: string;
  totalReferrals: number;
  affiliateBalance: number;
  depositTotal: number;
  withdrawalTotal?: number;
  netDeposit?: number;
  totalVolumeTraded: number;
}

export interface IBulkEditParams extends ICustomersFiltersDTO {
  selectedStatus?: number | null;
  conversionAgent?: string | null;
  retentionAgent?: string | null;
  search?: string;
  customerIds?: Array<number>;
  securityLevel?: SecurityLevel;
  isTest?: boolean;
}

export enum CustomerStatus {
  Active = 'ACTIVE',
  Disabled = 'DISABLED',
}

export interface IPersonalInfo {
  id: number;
  customerId: string;
  dateOfBirth: Date;
  nationality: string;
  countryOfResidence: string;
  idOrPassportNumber: string;
  address: string;
  city: string;
  zipCode: string;
  createdAt: Date;
  updatedAt: Date;
  isPep: boolean | null;
  isUsCitizenOrTaxResident: boolean | null;
}

export interface ITraderQuestionnaire {
  customerId: string;
  employment?: string;
  netWorth?: string;
  experience?: string;
  termsAcceptedAt?: Date | null;
  createdAt?: Date;
  updatedAt?: Date;
  result: string;
}
